/**
 * The curated universe.
 *
 * In the v2.2 bundle these 101 assets were 1,141 lines of `ASSETS.push(...)` calls
 * welded into the middle of atlas.js. They now live in `data/universe.json` and are
 * imported as data, so the universe can be edited, diffed, and validated without
 * touching code.
 */
import type { Asset } from './types.ts';
import universeJson from '../../data/universe.json' with { type: 'json' };

interface UniverseFile {
  version: string;
  asset_count: number;
  assets: Asset[];
}

const universe = universeJson as unknown as UniverseFile;

export const ASSETS: readonly Asset[] = Object.freeze(universe.assets);
export const UNIVERSE_VERSION = universe.version;

/**
 * Structural check over the loaded universe. Returns a list of human-readable
 * problems; empty means healthy. Run by the test suite, not at page load.
 */
export function validateUniverse(assets: readonly Asset[] = ASSETS): string[] {
  const problems: string[] = [];
  const seenCodes = new Set<string>();
  const seenTickers = new Map<string, string>();

  if (assets.length !== universe.asset_count) {
    problems.push(`asset_count says ${universe.asset_count} but ${assets.length} assets loaded`);
  }

  for (const a of assets) {
    const at = `${a.code} (${a.name})`;
    if (!a.code) problems.push(`asset with no code: ${a.name}`);
    if (seenCodes.has(a.code)) problems.push(`duplicate code ${a.code}`);
    seenCodes.add(a.code);

    if (!Array.isArray(a.principle_scores) || a.principle_scores.length !== 6) {
      problems.push(`${at}: expected 6 principle scores, got ${a.principle_scores?.length}`);
    }
    if (typeof a.score !== 'number' || a.score < 0 || a.score > 100) {
      problems.push(`${at}: score out of range: ${a.score}`);
    }
    if (a.sharia_bonus && a.score < a.raw_score) {
      problems.push(`${at}: sharia bonus applied but score ${a.score} < raw ${a.raw_score}`);
    }
    // A ticker colliding across two issuers would make findAsset order-dependent.
    for (const t of a.tickers ?? []) {
      const key = t.toUpperCase();
      const prior = seenTickers.get(key);
      if (prior && prior !== a.code) problems.push(`ticker ${t} shared by ${prior} and ${a.code}`);
      seenTickers.set(key, a.code);
    }
  }
  return problems;
}
